import * as z from "zod";

export const PLAN_IDS = ["starter", "team", "business"] as const;
export const PlanIdSchema = z.enum(PLAN_IDS);
export type PlanId = z.infer<typeof PlanIdSchema>;

export const BillingIntervalSchema = z.enum(["month", "year"]);
export type BillingInterval = z.infer<typeof BillingIntervalSchema>;

/** How a plan pays for models: included credits, or the customer's own keys. */
export const PLAN_FUNDINGS = ["credits", "byok"] as const;
export const PlanFundingSchema = z.enum(PLAN_FUNDINGS);
export type PlanFunding = z.infer<typeof PlanFundingSchema>;

export interface PlanDefinition {
  name: string;
  /** Monthly price of the credits plan in the catalog currency's minor unit (pence). */
  monthlyPrice: number;
  /** Monthly price of the same plan on the customer's own model keys, in pence. */
  byokMonthlyPrice: number;
  /** Bots included in the price; the Super bot is free and never counts. */
  bots: number;
  /** Model credit the credits plan includes each month, in US cents. Resets every month. */
  monthlyCreditCents: number;
  /** Organization members, pending invitations included; `null` is unlimited. */
  seats: number | null;
  /** The credits plan may also use members' own model keys. */
  byok: boolean;
  /** Spaces in the organization; `null` is unlimited. */
  spaces: number | null;
}

/**
 * The one source of truth for plans and their limits. Prices are display and validation values:
 * the billing provider holds the real prices and checks them against these.
 */
export const PlanCatalog = {
  currency: "gbp",
  /** Monthly price of each bot above the plan's included count, in pence. */
  extraBotMonthlyPrice: 1500,
  /** Yearly billing charges this many months fewer than twelve. */
  yearlyFreeMonths: 2,
  trialDays: 14,
  /** Limits that apply while the trial runs, before a plan is chosen. */
  trialPlan: "team",
  /** Days of the trial on which the owners are reminded to add a card. */
  trialReminderDays: [10, 13],
  /**
   * Model credits. Credits are kept in US dollars because providers bill in dollars. Every
   * amount marked PLACEHOLDER is for the owner to set before launch.
   */
  credits: {
    /** Usage is billed at provider cost times (1 + markup): 0.2 is cost plus 20%. */
    markup: 0.2,
    /** Credit a new company's trial runs on when the deployment has a model, in US cents. PLACEHOLDER. */
    trialCreditCents: 500,
    /** What one US cent of top-up credit costs in the catalog currency's minor unit. PLACEHOLDER. */
    topUpPricePerCreditCent: 0.8,
  },
  plans: {
    starter: {
      name: "Starter",
      monthlyPrice: 4900,
      byokMonthlyPrice: 3900, // PLACEHOLDER
      bots: 3,
      monthlyCreditCents: 2000, // PLACEHOLDER
      seats: 3,
      byok: true,
      spaces: 1,
    },
    team: {
      name: "Team",
      monthlyPrice: 14900,
      byokMonthlyPrice: 11900, // PLACEHOLDER
      bots: 10,
      monthlyCreditCents: 6000, // PLACEHOLDER
      seats: 15,
      byok: true,
      spaces: 1,
    },
    business: {
      name: "Business",
      monthlyPrice: 39900,
      byokMonthlyPrice: 31900, // PLACEHOLDER
      bots: 25,
      monthlyCreditCents: 15000, // PLACEHOLDER
      seats: null,
      byok: true,
      spaces: 5,
    },
  },
} as const satisfies {
  currency: string;
  extraBotMonthlyPrice: number;
  yearlyFreeMonths: number;
  trialDays: number;
  trialPlan: PlanId;
  trialReminderDays: readonly number[];
  credits: { markup: number; trialCreditCents: number; topUpPricePerCreditCent: number };
  plans: Record<PlanId, PlanDefinition>;
};

/** The price charged per interval for a monthly amount: yearly skips the free months. */
export function intervalPrice(monthlyPrice: number, interval: BillingInterval): number {
  return interval === "month" ? monthlyPrice : monthlyPrice * (12 - PlanCatalog.yearlyFreeMonths);
}

export function planPrice(
  plan: PlanId,
  interval: BillingInterval,
  funding: PlanFunding = "credits",
): number {
  const definition = PlanCatalog.plans[plan];
  return intervalPrice(
    funding === "byok" ? definition.byokMonthlyPrice : definition.monthlyPrice,
    interval,
  );
}

/** Whole micro-dollars billed for a provider cost: cost times (1 + markup), rounded up. */
export function creditChargeMicros(
  costUsd: number,
  markup: number = PlanCatalog.credits.markup,
): bigint {
  if (!Number.isFinite(costUsd) || costUsd < 0) throw new Error("Invalid model cost");
  if (!Number.isFinite(markup) || markup < 0) throw new Error("Invalid credit markup");
  // Basis points keep 0.2 exact; the micro rounding absorbs float noise in the provider cost.
  const costMicros = Math.round(costUsd * 1_000_000 * 1000) / 1000;
  const basisPoints = Math.round(markup * 10_000);
  return BigInt(Math.ceil((costMicros * (10_000 + basisPoints)) / 10_000 - 1e-9));
}

/** The top-up price in the catalog currency for an amount of credit in US cents. */
export function topUpPrice(creditCents: number): number {
  return Math.max(1, Math.round(creditCents * PlanCatalog.credits.topUpPricePerCreditCent));
}

/** Bots billed on top of the plan. */
export function extraBots(plan: PlanId, bots: number): number {
  return Math.max(0, bots - PlanCatalog.plans[plan].bots);
}

/** Why a plan refused a change. `subscription`: the trial is over and no plan is active. */
export const PLAN_LIMITS = ["bots", "seats", "spaces", "byok", "subscription"] as const;
export const PlanLimitSchema = z.object({
  reason: z.literal("plan_limit"),
  limit: z.enum(PLAN_LIMITS),
  plan: PlanIdSchema,
  /** The plan's allowance for `limit`, when it has a number. */
  allowed: z.number().int().nonnegative().nullable(),
});
export type PlanLimit = z.infer<typeof PlanLimitSchema>;

/** The plan limit an RPC error carries, so the client can offer an upgrade. */
export function planLimitFromError(error: unknown): PlanLimit | undefined {
  const data = error && typeof error === "object" && "data" in error ? error.data : undefined;
  const parsed = PlanLimitSchema.safeParse(data);
  return parsed.success ? parsed.data : undefined;
}

/** What the billing view shows about the organization's plan. */
export const PlanStatusSchema = z.object({
  /** The paid plan; `null` while the trial runs or after it ended. */
  id: PlanIdSchema.nullable(),
  /** How the paid plan funds models; absent from older servers. */
  funding: PlanFundingSchema.nullable().optional(),
  /** The trial ended unpaid: members read, bots pause, nothing is deleted. */
  readOnly: z.boolean().optional(),
  interval: BillingIntervalSchema.nullable(),
  /** The trial's end while no plan is paid. */
  trialEndsAt: z.string().nullable(),
  bots: z.object({ used: z.number().int().nonnegative(), included: z.number().int() }),
  seats: z.object({ used: z.number().int().nonnegative(), limit: z.number().int().nullable() }),
});
export type PlanStatus = z.infer<typeof PlanStatusSchema>;

/** Spend in US dollars: credits billed with the markup, or provider cost on an own key. */
const SpendUsdSchema = z.number().nonnegative();

/** Model spend this billing month, per teammate (bot) and per member. */
export const CreditUsageSchema = z.object({
  since: z.string(),
  totalUsd: SpendUsdSchema,
  bots: z.array(z.object({ botId: z.string(), name: z.string(), spentUsd: SpendUsdSchema })),
  members: z.array(z.object({ userId: z.string(), name: z.string(), spentUsd: SpendUsdSchema })),
});
export type CreditUsage = z.infer<typeof CreditUsageSchema>;

export const WEEKLY_SPEND_LIMIT_MAX_USD = 100_000;
export const WeeklySpendLimitUsdSchema = z
  .number()
  .finite()
  .min(0)
  .max(WEEKLY_SPEND_LIMIT_MAX_USD)
  .nullable();
/** `provider/model` ids a member's runs may use; empty allows every model. */
export const AllowedModelsSchema = z.array(z.string().trim().min(3).max(200)).max(200);

/** What admins set per member: a weekly spend limit and the models their runs may use. */
export const MemberSpendLimitSchema = z.object({
  userId: z.string(),
  name: z.string(),
  email: z.string(),
  role: z.string(),
  weeklySpendLimitUsd: WeeklySpendLimitUsdSchema,
  allowedModels: AllowedModelsSchema,
  spentThisWeekUsd: SpendUsdSchema,
});
export type MemberSpendLimit = z.infer<typeof MemberSpendLimitSchema>;

/** The model key a member's allowed list holds. */
export function allowedModelKey(provider: string, model: string): string {
  return `${provider}/${model}`;
}

/** Whether `allowed` (empty: every model) lets a run call this provider's model. */
export function modelAllowed(allowed: readonly string[], provider: string, model: string): boolean {
  return allowed.length === 0 || allowed.includes(allowedModelKey(provider, model));
}

/** Auto top-up: when credit falls below the threshold, the saved card buys `amountCents`. */
export const AutoTopUpSchema = z.object({
  thresholdCents: z.number().int().min(100).max(100_000),
  amountCents: z.number().int().positive(),
});
export type AutoTopUp = z.infer<typeof AutoTopUpSchema>;

/** Whether an auto top-up is due: it is on and the balance has fallen below its threshold. */
export function autoTopUpDue(
  balanceMicros: bigint,
  setting: { thresholdCents: number | null; amountCents: number | null },
): boolean {
  if (!setting.thresholdCents || !setting.amountCents) return false;
  return balanceMicros < BigInt(setting.thresholdCents) * 10_000n;
}

/**
 * The month of a plan's credits that contains `now`. A monthly plan's window is its billing
 * period; a yearly plan's year is cut into months counted back from its period end, so its
 * credits reset monthly too. Null once the period has ended.
 */
export function planCreditWindow(
  periodEndsAt: Date,
  interval: BillingInterval,
  now: Date,
): { key: string; endsAt: Date } | null {
  if (periodEndsAt <= now) return null;
  if (interval === "month") return { key: periodEndsAt.toISOString(), endsAt: periodEndsAt };
  let endsAt = periodEndsAt;
  for (let months = 1; months < 12; months++) {
    const earlier = new Date(periodEndsAt);
    earlier.setUTCMonth(earlier.getUTCMonth() - months);
    if (earlier <= now) break;
    endsAt = earlier;
  }
  return { key: endsAt.toISOString(), endsAt };
}
